/**
 * Учёт форм с несохранённым вводом.
 *
 * Обновление приложения перезагружает страницу, и всё, что человек набрал и не
 * отправил, пропадает. Чтобы спросить его до этого, нужно знать, есть ли такой
 * ввод. Отдельных «черновиков» в приложении нет, поэтому следим за самими
 * формами: любой ввод пользователя внутри `<form>` делает её «грязной».
 *
 * Ошибиться можно в две стороны. Лишний вопрос стоит человеку один клик, а
 * потерянное сообщение — нескольких минут работы, поэтому форма считается
 * «грязной», пока не сброшена (`reset`) или не убрана со страницы: после
 * отправки она может остаться на экране с теми же значениями, если сервер
 * отклонил данные.
 */
export class UnsavedChangesTracker {
  readonly #dirty = new Set<HTMLFormElement>();

  /** Начинает слежение за документом; возвращает функцию, которая его прекращает. */
  attach(root: Document = document): () => void {
    const markDirty = (event: Event) => {
      const target = event.target;
      if (!(target instanceof Element)) return;
      if (ignored(target)) return;
      const form = target.closest("form");
      if (form !== null) this.#dirty.add(form);
    };
    const markClean = (event: Event) => {
      if (event.target instanceof HTMLFormElement) {
        this.#dirty.delete(event.target);
      }
    };
    root.addEventListener("input", markDirty, true);
    root.addEventListener("change", markDirty, true);
    root.addEventListener("reset", markClean, true);

    return () => {
      root.removeEventListener("input", markDirty, true);
      root.removeEventListener("change", markDirty, true);
      root.removeEventListener("reset", markClean, true);
    };
  }

  /** Есть ли на экране форма, в которую что-то ввели и не сохранили. */
  hasUnsavedChanges(): boolean {
    for (const form of this.#dirty) {
      if (!form.isConnected) this.#dirty.delete(form);
    }

    return this.#dirty.size > 0;
  }
}

/**
 * Поля, ввод в которых не является данными для сохранения: поиск и фильтры
 * живут в адресе страницы, а скрытые поля человек не набирает.
 */
function ignored(target: Element): boolean {
  if (target.closest("[data-unsaved-ignore]") !== null) return true;

  return (
    target instanceof HTMLInputElement &&
    (target.type === "search" || target.type === "hidden")
  );
}
